// ─────────────────────────────────────────────────────────────────────────
// friendlyError — the single place that turns any thrown error into a
// message safe to show an end user. Nothing user-facing should ever print a
// raw "Firebase: Error (auth/…)", "Missing or insufficient permissions", or
// a bare "FirebaseError" string; route it through here instead.
//
// Trust model:
//  - Firebase *callable* errors (err.code like "functions/…") carry a message
//    our own Cloud Functions authored on purpose (HttpsError), e.g.
//    "Insufficient wallet balance. Please recharge first." Those are already
//    user-friendly, so we keep them.
//  - Auth SDK errors (err.code "auth/…") get mapped to plain language, and a
//    user simply closing the Google popup returns '' (show nothing).
//  - Anything else that looks like a raw SDK/internal string is replaced with
//    a generic fallback so implementation details never reach the user.
// ─────────────────────────────────────────────────────────────────────────

// Codes where the user cancelled themselves — not worth any message at all.
const SILENT = ['popup-closed-by-user', 'cancelled-popup-request', 'user-cancelled'];

const AUTH_MESSAGES = [
  ['popup-blocked', 'Your browser blocked the sign-in popup. Allow popups for this site and try again.'],
  ['email-already-in-use', 'That email is already registered — try signing in instead.'],
  ['invalid-credential', 'Incorrect email or password.'],
  ['wrong-password', 'Incorrect email or password.'],
  ['invalid-email', 'Please enter a valid email address.'],
  ['user-not-found', 'No account found with that email.'],
  ['user-disabled', 'This account has been disabled. Contact support if you think this is a mistake.'],
  ['weak-password', 'Password should be at least 6 characters.'],
  ['requires-recent-login', 'For your security, please sign in again and then retry.'],
  ['too-many-requests', 'Too many attempts. Please wait a moment and try again.'],
  ['network-request-failed', 'Network problem — check your connection and try again.'],
  ['account-exists-with-different-credential', 'This email is already registered with a different sign-in method. Try that method instead.'],
  ['operation-not-allowed', 'This sign-in method isn’t available right now. Please try another way.'],
];

// Heuristic for "this message is a raw SDK/internal string, not something we
// wrote for a human": Firebase prefixes and the generic permission/internal
// wordings the SDK emits. Deliberately does NOT do a catch-all "word/word"
// scan for error-code-shaped fragments (e.g. auth/…, storage/…) — real SDK
// codes always show up wrapped as "Firebase: Error (auth/…)." which the
// /firebase/i and /Error\s*\(/ checks below already catch. A bare
// "word/word" regex is too broad: it also matches ordinary human-written
// text (e.g. a topic named "series/parallel circuits" inside a validation
// message like "series/parallel circuits failed checks: …"), which caused
// legitimate, actionable error messages to be discarded in favor of the
// generic fallback — e.g. the marketplace "Approve current version" flow's
// content-validation errors were getting masked as "Could not approve
// version." with no indication of what actually failed.
function looksRaw(message) {
  if (!message) return true;
  return (
    /firebase/i.test(message) ||
    /Error\s*\(/.test(message) ||                  // "Error (auth/…)."
    /missing or insufficient permissions/i.test(message) ||
    /^internal$/i.test(message.trim())
  );
}

/**
 * friendlyError(err, fallback?) → string safe to show an end user.
 * Returns '' when the failure is a user-initiated cancel (caller should then
 * simply show nothing).
 */
export function friendlyError(err, fallback = 'Something went wrong. Please try again.') {
  const code = String(err?.code || '');
  const message = String(err?.message || '');

  if (SILENT.some((c) => code.includes(c))) return '';

  for (const [fragment, text] of AUTH_MESSAGES) {
    if (code.includes(fragment)) return text;
  }

  // Our own Cloud Functions author friendly HttpsError messages — trust them.
  if (code.startsWith('functions/')) return message || fallback;

  // Otherwise only surface the message if it reads like real human text.
  return looksRaw(message) ? fallback : message;
}

// Back-compat alias for the sign-in screen's original name.
export const humanizeAuthError = (err) => friendlyError(err, 'Something went wrong. Please try again.');
